import { Injectable } from '@angular/core';
import { catchError, tap } from 'rxjs/operators';

import { Action, State, StateContext } from '@ngxs/store';

import { BcCalendarColumnMode, BcCalendarDisplayMode, BcCalendarSettings } from '@bayclubs/calendar';
import { BcStateContext, BcStateForm, BcStateListContext } from '@bayclubs/state';

import { GroupClassesApiService } from '@shared/shared.module';

import { CalendarMode } from '@bayclubs/group-classes-employee-api';
import { LocationSortingService } from '../../shared/services/location-sorting.service';
import { CalendarEventComponent } from '../components/calendar/calendar-event/calendar-event.component';
import { ClassesCalendarEventBuilderService } from '../services/classes-calendar-event-builder.service';
import * as actions from './classes-calendar.actions';
import { CLASSES_CALENDAR_FEATURE_KEY, ClassesCalendarStateModel } from './classes-calendar.state.model';

const initialState = {
    calendarEvents: BcStateListContext.Empty(),
    calendarResources: BcStateListContext.Empty(),
    calendarSettings: BcStateContext.Empty(),
    filters: BcStateForm.Empty(),
    selectedClubId: null,
} as ClassesCalendarStateModel;

@Injectable({ providedIn: 'root' })
@State({ name: CLASSES_CALENDAR_FEATURE_KEY, defaults: initialState })
export class ClassesCalendarState {
    constructor(
        private groupClassesApi: GroupClassesApiService,
        private eventsBuilderService: ClassesCalendarEventBuilderService,
        private locationSorting: LocationSortingService
    ) {}

    @Action(actions.LoadClassesCalendarAction)
    loadClassesCalendar(ctx: StateContext<ClassesCalendarStateModel>, action: actions.LoadClassesCalendarAction) {
        const state = ctx.getState();
        return this.groupClassesApi.getCalendar(action.query).pipe(
            tap(calendarModel => {
                this.locationSorting.sortLocations(calendarModel.locations);

                let filterLocationIds = action.query.locationIds;
                const shouldNotAddOtherResource =
                    filterLocationIds && filterLocationIds.length > 0 && filterLocationIds.every(x => x != null);

                if (filterLocationIds && filterLocationIds.length > 0) {
                    calendarModel.locations = calendarModel.locations.filter(
                        l => filterLocationIds.some(filterLocationId => filterLocationId === l.id) && l.id != null
                    );
                }

                const resources = this.eventsBuilderService.buildCourtResources(
                    calendarModel.locations,
                    calendarModel.events,
                    shouldNotAddOtherResource
                );
                const events = this.eventsBuilderService.buildEvents(calendarModel.events, resources, state.selectedClubId);
                const settings = {
                    dateFrom: { value: calendarModel.dateFrom.value },
                    dateTo: { value: calendarModel.dateTo.value },
                    displayMode:
                        action.query.calendarMode.toString() === CalendarMode.Day.toString()
                            ? BcCalendarDisplayMode.Day
                            : BcCalendarDisplayMode.Week,
                    timeFromInMinutes: 300,
                    timeToInMinutes: 1380,
                    userResourceAvailability: false,
                    showCancelledEventsAsPill: false,
                    showCurrentTimeIndicator: false,
                    columnMode: BcCalendarColumnMode.Auto,
                    eventComponent: CalendarEventComponent.componentName,
                    blockAddInPast: true,
                    showCurrentDayIndicator: true,
                } as BcCalendarSettings;
                ctx.dispatch(new actions.LoadClassesCalendarSuccessAction(events, resources, settings));
            }),
            catchError(err => {
                throw err;
            })
        );
    }

    @Action(actions.LoadClassesCalendarSuccessAction)
    loadClassesCalendarSuccess(ctx: StateContext<ClassesCalendarStateModel>, action: actions.LoadClassesCalendarSuccessAction) {
        ctx.patchState({
            calendarEvents: BcStateListContext.Loaded(action.events),
            calendarResources: BcStateListContext.Loaded(action.resources),
            calendarSettings: BcStateContext.Loaded(action.settings),
        });
    }

    @Action(actions.InitClassesCalendarFiltersFormAction)
    initClassesCalendarFilters(ctx: StateContext<ClassesCalendarStateModel>) {}

    @Action(actions.SetSelectedClubId)
    setSelectedClubId(ctx: StateContext<ClassesCalendarStateModel>, action: actions.SetSelectedClubId) {
        ctx.patchState({ selectedClubId: action.clubId });
    }
}
